/**
 * MemberGivingPage — a member's own giving record.
 *
 * Their tithes and offerings only: the church ledger is matched to them by
 * their own contact details, so nobody sees anyone else's giving.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Loader2, HandCoins, Receipt, FileText } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';
import GiverStatement from '@/components/church/GiverStatement';
import GivingLetterDialog from '@/components/church/GivingLetterDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const THIS_YEAR = new Date().getFullYear();

const fetchJson = async (path) => {
  const r = await fetch(`${API_URL}${path}`, { headers: authHeaders() });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Could not load your giving');
  return r.json();
};

const money = (n, cur) => `${cur || ''} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`.trim();

export default function MemberGivingPage() {
  const navigate = useNavigate();
  const [year, setYear] = useState('all');
  const [letterOpen, setLetterOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['member-giving', year],
    queryFn: () => fetchJson(`/api/member/contributions${year === 'all' ? '' : `?year=${year}`}`),
    retry: false,
  });

  const rows = data?.contributions || [];
  const summary = data?.summary || [];
  // Currencies are never added together — a member reading one merged number
  // cannot tell which of their gifts it describes.
  const byCurrency = Object.entries(data?.by_currency || {});

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/member')}
                  data-testid="member-giving-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <HandCoins className="h-5 w-5 text-amber-600" /> My giving
            </h1>
            <p className="text-xs text-slate-500">Everything recorded against your name.</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4" data-testid="member-giving-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex flex-wrap gap-2" data-testid="member-giving-total">
            {byCurrency.length ? byCurrency.map(([ccy, t]) => (
              <div key={ccy} className="rounded-xl border border-slate-200 bg-white px-4 py-3"
                   data-testid={`member-giving-total-${ccy}`}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  Given in {ccy}
                </p>
                <p className="text-2xl font-semibold text-slate-900">
                  {money(t.total, ccy)}
                </p>
              </div>
            )) : (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Given in total</p>
                <p className="text-2xl font-semibold text-slate-900">—</p>
              </div>
            )}
          </div>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-40" data-testid="member-giving-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              {[THIS_YEAR, THIS_YEAR - 1, THIS_YEAR - 2].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setLetterOpen(true)}
                  data-testid="member-giving-letter-btn">
            <FileText className="mr-1.5 h-4 w-4" />Year-end letter
          </Button>
        </div>

        {summary.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="member-giving-summary">
            {summary.map((s) => (
              <Badge key={`${s._id}-${s.currency}`} variant="outline" className="bg-white capitalize">
                {s._id || 'other'}: {money(s.total, s.currency)} · {s.count}
              </Badge>
            ))}
          </div>
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Your gifts</CardTitle>
            <CardDescription>Tithes, offerings and pledges recorded by your church.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="py-12 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
            ) : error ? (
              <p className="py-12 text-center text-sm text-rose-600" data-testid="member-giving-error">{error.message}</p>
            ) : rows.length === 0 ? (
              <div className="py-12 text-center" data-testid="member-giving-empty">
                <Receipt className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">Nothing recorded yet.</p>
                <p className="text-xs text-slate-400 mt-1">
                  Gifts appear here once your church records them against your name.
                </p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
                    <th className="text-left py-2 px-4">Date</th>
                    <th className="text-left py-2 px-4">What for</th>
                    <th className="text-left py-2 px-4">How</th>
                    <th className="text-right py-2 px-4">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id} className="border-b last:border-0" data-testid={`member-gift-${c.id}`}>
                      <td className="py-2 px-4 text-slate-600">{c.date || '—'}</td>
                      <td className="py-2 px-4 capitalize">{c.category || 'offering'}</td>
                      <td className="py-2 px-4 text-xs text-slate-500 capitalize">{c.payment_method || '—'}</td>
                      <td className="py-2 px-4 text-right font-medium">{money(c.amount, c.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* What they have promised, and what is still to come. Pledges live on
            the one canonical record, so this agrees with the assembly's book. */}
        <Card data-testid="member-pledges-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">My pledges</CardTitle>
            <CardDescription>
              What you have promised, across every church and appeal
            </CardDescription>
          </CardHeader>
          <CardContent>
            <GiverStatement mine />
          </CardContent>
        </Card>
      </main>

      {letterOpen ? (
        <GivingLetterDialog mine onClose={() => setLetterOpen(false)} />
      ) : null}
    </div>
  );
}
